import React from 'react';
import { observer } from 'mobx-react';
import { CapacityPlannerScreen } from '../../screen/CapacityPlanner/Screen';
import { projectColor, userColor, isToday, isWeekend } from './types';

interface Props { planner: CapacityPlannerScreen; }

@observer
export class Timeline extends React.Component<Props> {
    render() {
        const p = this.props.planner;
        return (
            <main className="timeline-container">
                <div className="view-tabs">
                    <button className={'tab' + (p.currentView === 'projects' ? ' active' : '')} data-test-cp-tab-projects onClick={() => p.switchView('projects')}>Projects Overview</button>
                    <button className={'tab' + (p.currentView === 'developers' ? ' active' : '')} data-test-cp-tab-developers onClick={() => p.switchView('developers')}>Developer View</button>
                </div>
                <div className="timeline-header">
                    <div className="timeline-controls">
                        <button className="btn btn-sm" onClick={p.zoomOut}>-</button>
                        <span className="zoom-level">{p.zoomLabel}</span>
                        <button className="btn btn-sm" onClick={p.zoomIn}>+</button>
                        <span className="week-nav-separator">|</span>
                        <button className="btn btn-sm" data-test-cp-prev-week onClick={p.prevWeek}>←</button>
                        <span className="week-label">{p.currentWeekLabel}</span>
                        <button className="btn btn-sm" data-test-cp-next-week onClick={p.nextWeek}>→</button>
                        <button className="btn btn-sm" data-test-cp-today onClick={p.goToCurrentWeek}>Today</button>
                    </div>
                </div>
                <div className="timeline-wrapper">
                    {p.currentView === 'projects' ? this.renderProjectsView() : this.renderDevelopersView()}
                </div>
            </main>
        );
    }

    renderTimelineDates(dates: Date[], width: number) {
        return (
            <div className="timeline-dates">
                {dates.map((date, i) => (
                    <div key={i} className={'timeline-date' + (isToday(date) ? ' today' : '') + (isWeekend(date) ? ' weekend' : '')} style={{ width }}>
                        <span className="month">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                        <span className="day">{date.getDate()}</span>
                    </div>
                ))}
            </div>
        );
    }

    renderTimelineGrid(dates: Date[], width: number) {
        return (
            <div className="timeline-grid">
                {dates.map((date, i) => (
                    <div key={i} className={'timeline-grid-line' + (isToday(date) ? ' today' : '')} style={{ left: i * width }} />
                ))}
            </div>
        );
    }

    renderProjectsView() {
        const p = this.props.planner;
        const dates = p.getTimelineDates();
        const width = p.dayWidth;
        const totalWidth = dates.length * width;
        return (
            <>
                <div className="timeline-labels">
                    {p.projects.map(proj => {
                        const allocs = p.getProjectAllocations(proj);
                        const deadlineStatus = p.getDeadlineStatus(proj);
                        return (
                            <div key={proj.id} className="timeline-label" data-test-cp-project={proj.id} onClick={() => p.openProjectPanel(proj.id as number)}>
                                <div className="project-color" style={{ background: projectColor(proj) }} />
                                <div className="project-info">
                                    <div className="project-name">{proj.name}</div>
                                    <div className="project-meta">
                                        {allocs.length} devs{deadlineStatus?.status === 'exceeded' ? ' · ⚠️' : ''}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="timeline-scroll" id="timeline-scroll">
                    {this.renderTimelineDates(dates, width)}
                    {this.renderTimelineGrid(dates, width)}
                    <div className="timeline-content" style={{ width: totalWidth }}>
                        {p.projects.map(proj => {
                            const pStart = p.getProjectStartDate(proj);
                            const pEnd = p.getProjectEndDate(proj);
                            const startOffset = p.daysBetween(p.startDate, pStart);
                            const duration = p.daysBetween(pStart, pEnd);
                            const left = startOffset * width;
                            const barWidth = Math.max(duration * width, 60);
                            const deadlineStatus = p.getDeadlineStatus(proj);
                            const deadline = p.getProjectDeadline(proj);
                            let deadlineMarker = null;
                            if (deadline) {
                                const deadlineOffset = p.daysBetween(pStart, deadline);
                                const deadlineLeft = deadlineOffset * width;
                                if (deadlineLeft > 0 && deadlineLeft <= barWidth) {
                                    deadlineMarker = <div className="deadline-marker" style={{ left: deadlineLeft }} />;
                                }
                            }
                            const allocs = p.getProjectAllocations(proj);
                            const dots = allocs.slice(0, 3).map(a => {
                                const u = p.users.find(ux => ux.id === (a as any).user?.id) as any;
                                if (!u) return null;
                                return (
                                    <div key={a.id} className="allocation-dot" style={{ background: userColor(u) }} title={`${u.fullName}: ${p.getDaysPerWeek(a)} days/week`}>
                                        {(u.fullName || '?').charAt(0)}
                                    </div>
                                );
                            });
                            return (
                                <div key={proj.id} className="timeline-row">
                                    <div
                                        className={'project-bar' + (deadlineStatus?.status === 'exceeded' ? ' deadline-exceeded' : '') + (p.selectedProjectId === proj.id ? ' selected' : '')}
                                        data-test-cp-project-bar
                                        style={{ left, width: barWidth, background: projectColor(proj) }}
                                        onClick={() => p.openProjectPanel(proj.id as number)}
                                        onDoubleClick={() => p.openDetailView(proj.id as number)}
                                    >
                                        <span>{proj.name}</span>
                                        <div className="allocation-dots">{dots}</div>
                                        {deadlineMarker}
                                        <div className="resize-handle left" onMouseDown={(e) => p.startProjectResize(e)} />
                                        <div className="resize-handle right" onMouseDown={(e) => p.startProjectResize(e)} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </>
        );
    }

    renderDevelopersView() {
        const p = this.props.planner;
        const dates = p.getTimelineDates();
        const width = p.dayWidth;
        const totalWidth = dates.length * width;
        return (
            <>
                <div className="timeline-labels">
                    {p.users.map(u => {
                        const uAny = u as any;
                        const allocation = p.getDeveloperAllocation(u.id as number);
                        return (
                            <div key={u.id} className="developer-label">
                                <div className="dev-avatar" style={{ background: userColor(uAny) }}>{(uAny.fullName || '?').charAt(0)}</div>
                                <div className="dev-info">
                                    <div className="dev-name">{uAny.fullName}</div>
                                    <div className={'dev-allocation' + (allocation.isOverallocated ? ' overallocated' : '')}>{allocation.totalDays}/5 days</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="timeline-scroll" id="timeline-scroll">
                    {this.renderTimelineDates(dates, width)}
                    {this.renderTimelineGrid(dates, width)}
                    <div className="timeline-content" style={{ width: totalWidth }}>
                        {p.users.map(u => {
                            const uAny = u as any;
                            const allocation = p.getDeveloperAllocation(u.id as number);
                            const availabilityCells = dates.map((date, i) => {
                                const avail = p.getAvailabilityForDate(uAny, date);
                                return <div key={i} className={'availability-cell ' + avail} style={{ width }} />;
                            });
                            const bars = allocation.projects.map(pa => {
                                const proj = p.projects.find(pr => pr.id === pa.projectId);
                                if (!proj) return null;
                                const pStart = p.getProjectStartDate(proj);
                                const pEnd = p.getProjectEndDate(proj);
                                const startOffset = p.daysBetween(p.startDate, pStart);
                                const duration = p.daysBetween(pStart, pEnd);
                                const left = startOffset * width;
                                const barWidth = Math.max(duration * width, 60);
                                const height = Math.min((pa.daysPerWeek / 5) * 36, 36);
                                return (
                                    <div key={pa.projectId} className="project-bar" data-test-cp-project-bar
                                        style={{ left, width: barWidth, height, top: (40 - height) / 2 + 4, background: pa.projectColor }}
                                        onClick={() => p.openProjectPanel(proj.id as number)}
                                        onDoubleClick={() => p.openDetailView(proj.id as number)}
                                        title={`${proj.name}: ${pa.daysPerWeek} days/week`}>
                                        <span style={{ fontSize: '0.7rem' }}>{proj.name}</span>
                                    </div>
                                );
                            });
                            return (
                                <div key={u.id} className="timeline-row" style={{ height: 70 }}>
                                    {bars}
                                    <div className="availability-row">{availabilityCells}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </>
        );
    }
}
